import { useHeadshotManager } from '@src/hooks/useHeadshotManager';
import { useDatoClient } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { orderBy } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import ChatGPTService from '../../services/ChatGPTService';
import BackIcon from '../../svgs/BackIcon';
import CloseIcon from '../../svgs/CloseIcon';
import PhotoIcon from '../../svgs/PhotoIcon';
import { PhotoTab } from '../../types.ts/story';
import applyImageOptimizations from '../../utility/applyImageOptimizations';
import { generateImagesWithGPT } from '../../utility/processGPTData';
import { getAttributionForPhoto, searchPhotos } from '../../utility/unsplash';
import SearchInput from '../sidepanel/SearchInput';
import SpinningLoading from '../SpinningLoading';
import EnlargedImageRenderProp from './EnlargedImageRenderProp';
import { PICKER_MODAL_INSET } from './pickerModalStyles';
import SelectedCheckBadge from './SelectedCheckBadge';
import ShareableImageView from './ShareableImageView';
import Tabs from './Tabs';
import { AuthorAttribution, UnsplashAttribution } from './UnsplashAttributions';

import type { ImageKey, ImageWithType } from '../../types.ts/general';
import type { Artifact } from '../../types.ts/story';
import type { Attribution } from '../../utility/unsplash';
import type { Dispatch, SetStateAction } from 'react';

const logger = LogManager.getLogger('component:PhotosModal');
export type PhotoModalOtherFields =
  | 'ai'
  | 'stock'
  | 'quotes'
  | 'org_photos'
  | 'org_logos'
  | 'org_videos'
  | 'headshots';

type Props = {
  /** When provided, a back button renders top-left and returns to the previous
      modal. Omit when there is nothing to go back to — a back button that only
      closes duplicates the close button. */
  openPrevModal?: () => void;
  onCloseSelf: () => void;
  modalItemType?: ImageKey;
  otherFields?: Array<PhotoModalOtherFields>;
  externalPhotos?: {
    ai?:
      | (Img & {
          id: string;
        })[]
      | undefined;
    stock: Img[] | undefined;
  };
  isLoading?: boolean;
  setSelectedImage: (img: ImageWithType[ImageKey] | null) => void;
  selectedImageUrl: string | null;
  keywords?: string[];
  setKeywords?: Dispatch<SetStateAction<string[]>>;
  dallePrompt?: string;
  setDallePrompt?: Dispatch<SetStateAction<string>>;
  TopComponent?: React.ComponentType<{
    tab: PhotoTab;
    onHeadshotUpload?: (file: File) => Promise<void>;
    isUploadingHeadshot?: boolean;
  }>;
  BottomComponent?: React.ComponentType<{ tab: PhotoTab }>;
  searchBarRadius?: string;
  searchText?: string;
  setSearchText?: Dispatch<SetStateAction<string>>;
  origin?: string;
  showDescription?: boolean;
};

export type Img = {
  url: string;
  alt: string;
  width: string;
  height: string;
  description?: string;
  attribution?: Attribution;
  createdAt?: string;
};

type QuoteImg = Img & {
  quote: string;
  storytellerName?: string;
  shareableImageId: string;
};

type Asset<T extends PhotoTab> = {
  key: T;
  value: T extends PhotoTab.ai ? (Img & { id: string })[] : Img[];
};

/** Display labels for tabs whose enum value isn't the label to show. */
const TAB_LABELS: Partial<Record<PhotoTab, string>> = {
  [PhotoTab.quote]: 'Shareable Images',
  [PhotoTab.ai]: 'AI',
};

const mapArtifactToPhotoAsset = (artifact: Artifact) =>
  ({
    id: artifact.id,
    url: artifact.url,
    alt: artifact.title,
    width: artifact.width,
    height: artifact.height,
    description: artifact.title,
    createdAt: artifact._createdAt,
  }) as Img;

const prepareArtifacts = (artifacts?: Artifact[]) =>
  orderBy(
    artifacts?.filter(a => a.responsiveImage).map(mapArtifactToPhotoAsset) ||
      [],
    'createdAt',
    'desc',
  );

const prepareVideoArtifacts = (artifacts?: Artifact[]) =>
  orderBy(artifacts?.map(mapArtifactToPhotoAsset) || [], 'createdAt', 'desc');

const PhotoModal = observer((props: Props) => {
  const {
    otherFields = [],
    searchBarRadius,
    modalItemType,
    TopComponent,
    BottomComponent,
  } = props;
  const videoCreator = useVideoCreatorStore();
  const datoCientStore = useDatoClient();

  let hasOrgLogos = otherFields.includes('org_logos');
  const [stockItems, setStockItems] = useState<Img[] | undefined>(undefined);
  const [aiItems, setAiItems] = useState<Img[] | undefined>(undefined);
  const [stockLoading, setStockLoading] = useState<boolean>(false);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const { keywords, setKeywords } = props;

  let hasStocks = !!(
    otherFields.includes('stock') || props.externalPhotos?.stock
  );
  let hasAis =
    !!(otherFields.includes('ai') || props.externalPhotos?.ai) &&
    !videoCreator.disableAiImageGenerate;
  let hasQuotes = otherFields.includes('quotes');
  let hasOrgArtifacts = otherFields.includes('org_photos');
  let hasOrgVideos = false;
  if (hasOrgLogos) {
    hasStocks = false;
    hasAis = false;
    hasQuotes = false;
    hasOrgArtifacts = false;
    hasOrgVideos = false;
  }

  if (otherFields.includes('org_videos')) {
    hasOrgVideos = true;
    hasOrgLogos = false;
  }

  const hasHeadshots = otherFields.includes('headshots');

  // Speaker headshots — delegate to dedicated hook
  const {
    headshotImages,
    headshotSpeakers,
    isUploadingHeadshot,
    uploadSpeakerId,
    setUploadSpeakerId,
    handleHeadshotUpload,
  } = useHeadshotManager(hasHeadshots);

  // Keep search terms in state
  const [artifactSearchTerm, setArtifactSearchTerm] = useState('');
  const [orgArtifactSearchTerm, setOrgArtifactSearchTerm] = useState('');
  const [orgLogoSearchTerm, setOrgLogoSearchTerm] = useState('');
  const [orgVideoSearchTerm, setOrgVideoSearchTerm] = useState('');
  const [storyVideoSearchTerm, setStoryVideoSearchTerm] = useState('');

  // Get original data from source
  const artifactsOriginal = useMemo(
    () => prepareArtifacts(videoCreator.story?.storyArtifacts),
    [videoCreator.story?.storyArtifacts],
  );

  const stock = useMemo(
    () => prepareArtifacts(videoCreator?.story?.storyAssets),
    [videoCreator?.story?.storyAssets],
  );

  const ai = useMemo(
    () => prepareArtifacts(videoCreator?.story?.aiPhotos),
    [videoCreator?.story?.aiPhotos],
  );

  const allReferencingShowcases = videoCreator?.story?._allReferencingShowcases;
  const _orgArtifacts = allReferencingShowcases?.flatMap(
    album => album.organizationArtifacts || [],
  );
  const organizationArtifactsOriginal = useMemo(
    () => prepareArtifacts(_orgArtifacts),
    [_orgArtifacts],
  );

  const _orgLogos = allReferencingShowcases?.flatMap(
    album => album.organizationLogos || [],
  );
  const organizationLogosOriginal = useMemo(
    () => prepareArtifacts(_orgLogos),
    [_orgLogos],
  );

  // Compute filtered lists based on search terms
  const artifact = useMemo(() => {
    if (!artifactSearchTerm) return artifactsOriginal;
    return artifactsOriginal.filter(a =>
      a.description?.toLowerCase().includes(artifactSearchTerm.toLowerCase()),
    );
  }, [artifactsOriginal, artifactSearchTerm]);

  const organizationArtifacts = useMemo(() => {
    if (!orgArtifactSearchTerm) return organizationArtifactsOriginal;
    return organizationArtifactsOriginal.filter(a =>
      a.description
        ?.toLowerCase()
        .includes(orgArtifactSearchTerm.toLowerCase()),
    );
  }, [organizationArtifactsOriginal, orgArtifactSearchTerm]);

  const organizationLogos = useMemo(() => {
    if (!orgLogoSearchTerm) return organizationLogosOriginal;
    return organizationLogosOriginal.filter(a =>
      a.description?.toLowerCase().includes(orgLogoSearchTerm.toLowerCase()),
    );
  }, [organizationLogosOriginal, orgLogoSearchTerm]);

  const _storyVideos = useMemo(
    () => prepareVideoArtifacts(videoCreator.story?.storyArtifactsVideo),
    [videoCreator.story?.storyArtifactsVideo],
  );

  const storyVideos = useMemo(() => {
    if (!storyVideoSearchTerm) return _storyVideos;
    return _storyVideos.filter(a =>
      a.description?.toLowerCase().includes(storyVideoSearchTerm.toLowerCase()),
    );
  }, [_storyVideos, storyVideoSearchTerm]);

  const _orgVideos = useMemo(() => {
    return prepareVideoArtifacts(
      allReferencingShowcases?.flatMap(
        album => album.organizationArtifactsVideo || [],
      ),
    );
  }, [allReferencingShowcases]);

  const organizationVideos = useMemo(() => {
    if (!orgVideoSearchTerm) return _orgVideos;
    return _orgVideos.filter(a =>
      a.description?.toLowerCase().includes(orgVideoSearchTerm.toLowerCase()),
    );
  }, [_orgVideos, orgVideoSearchTerm]);

  const handleGoBack = () => {
    props.openPrevModal?.();
    props.onCloseSelf();
  };

  const assetsAndTabs = React.useMemo(() => {
    let assets: Asset<PhotoTab>[];
    let tabHeader: PhotoTab[];

    if (hasOrgVideos) {
      assets = [
        {
          key: PhotoTab.orgVideos,
          value: organizationVideos,
        },
        {
          key: PhotoTab.storyVideos,
          value: storyVideos,
        },
      ];
      tabHeader = [PhotoTab.orgVideos, PhotoTab.storyVideos];
      return { assets, tabHeader };
    }

    if (hasOrgLogos) {
      const isCompositionSettings =
        videoCreator.openPhotoElementReplacementModal?.origin ===
        'composition-settings';
      assets = [
        ...(isCompositionSettings
          ? [
              {
                key: PhotoTab.stock,
                value: stockItems || stock || props.externalPhotos?.stock || [],
              },
              { key: PhotoTab.orgArtifact, value: organizationArtifacts },
              { key: PhotoTab.artifact, value: artifact },
            ]
          : []),
        {
          key: PhotoTab.logo,
          value: organizationLogos,
        },
      ];
      tabHeader = [
        ...(isCompositionSettings
          ? [PhotoTab.artifact, PhotoTab.orgArtifact, PhotoTab.stock]
          : []),
        PhotoTab.logo,
      ];
    } else {
      assets = [
        {
          key: PhotoTab.stock,
          value: stockItems || stock || props.externalPhotos?.stock || [],
        },
        { key: PhotoTab.artifact, value: artifact },
      ];
      tabHeader = [PhotoTab.artifact, PhotoTab.stock];
    }

    if (hasAis) {
      assets.push({
        key: PhotoTab.ai,
        value: aiItems || ai || props.externalPhotos?.ai || [],
      });
      tabHeader.push(PhotoTab.ai);
    }

    if (hasQuotes) {
      const quoteImages = videoCreator.story?.shareableImages
        ?.filter(s => s.imagefile)
        .map(s => ({
          imagefile: s.imagefile,
          quote: s.quote,
          storytellerName: s.storytellerName,
          id: s.id,
        }))
        ?.map(a => ({
          id: a?.imagefile?.id,
          alt: a?.imagefile?.title,
          url: a?.imagefile?.url,
          width: a?.imagefile?.width,
          height: a?.imagefile?.height,
          quote: a.quote,
          storytellerName: a.storytellerName,
          shareableImageId: a.id,
        })) as QuoteImg[];

      assets.push({
        key: PhotoTab.quote,
        value: quoteImages || [],
      });
      tabHeader.push(PhotoTab.quote);
    }

    if (hasOrgArtifacts) {
      assets.push({
        key: PhotoTab.orgArtifact,
        value: organizationArtifacts,
      });
      tabHeader.splice(1, 0, PhotoTab.orgArtifact);
    }

    if (hasHeadshots) {
      assets.unshift({
        key: PhotoTab.headshot,
        value: headshotImages,
      });
      tabHeader.unshift(PhotoTab.headshot);
    }

    return { assets, tabHeader };
  }, [
    hasOrgVideos,
    hasOrgLogos,
    hasAis,
    hasQuotes,
    hasOrgArtifacts,
    hasHeadshots,
    headshotImages,
    stockItems,
    stock,
    props.externalPhotos?.stock,
    artifact,
    aiItems,
    ai,
    props.externalPhotos?.ai,
    organizationLogos,
    organizationArtifacts,
    organizationVideos,
    storyVideos,
    videoCreator.story?.shareableImages,
  ]);

  const assets = assetsAndTabs.assets;
  const tabHeader = assetsAndTabs.tabHeader;

  const [tab, setTab] = useState(() => {
    if (hasHeadshots) return PhotoTab.headshot;
    if (modalItemType === 'stock') return PhotoTab.stock;
    if (hasOrgLogos) {
      const isCompositionSettings =
        videoCreator.openPhotoElementReplacementModal?.origin ===
        'composition-settings';
      if (isCompositionSettings && artifact.length) return PhotoTab.artifact;
      return PhotoTab.logo;
    }
    // For quotecard origin, default to org photos tab if available
    if (
      props.origin === 'quotecard' &&
      hasOrgArtifacts &&
      organizationArtifacts.length
    )
      return PhotoTab.orgArtifact;
    if (artifact.length) return PhotoTab.artifact;
    if (hasOrgArtifacts && organizationArtifacts.length)
      return PhotoTab.orgArtifact;
    if (hasStocks) return PhotoTab.stock;
    if (hasOrgVideos) return PhotoTab.storyVideos;
    return PhotoTab.stock;
  });

  const [unsplashSearch, setUnsplashSearch] = useState(
    keywords ? keywords.join(' ') : '',
  );
  const [unsplashPage, setUnsplashPage] = useState(1);
  const handleSearchStock = async (textValue: string) => {
    setUnsplashSearch(textValue);
    setUnsplashPage(1);
  };
  useEffect(() => {
    const fetchUnsplashPhotos = async () => {
      if (!unsplashSearch) {
        setStockItems(undefined);
        setUnsplashPage(1);
        setUnsplashSearch('');
        return;
      }
      try {
        setStockLoading(true);
        const { photos } = await searchPhotos({
          query: unsplashSearch,
          orientation: 'landscape',
          page: unsplashPage,
          perPage: 9,
        });
        const stockData = (photos?.map(res => ({
          id: res.id,
          url: res.urls.regular,
          alt: res.alt_description,
          width: res.width,
          height: res.height,
          description: res.description || res.alt_description,
          attribution: getAttributionForPhoto(res),
        })) || []) as unknown as Img[];
        setStockItems(_items =>
          unsplashPage === 1 ? stockData : (_items || []).concat(stockData),
        );
      } catch (error) {
        logger.error('Error fetching Unsplash photos', { error });
      } finally {
        setStockLoading(false);
      }
    };
    fetchUnsplashPhotos();
  }, [unsplashSearch, unsplashPage]);

  const handleAiSearch = async (dallePrompt: string) => {
    if (!dallePrompt.length) {
      return setAiItems([]);
    }
    try {
      setAiLoading(true);
      const gptService = new ChatGPTService(
        videoCreator,
        datoCientStore.aiPromptRepository,
      );
      const arborStylePrompt =
        await gptService.fetchAiPrompt('Arbor Photo Style');

      // Build the prompt with speaker context if specified
      let promptText = dallePrompt;

      // Final prompt with Arbor style
      let prompt = `${promptText} ${arborStylePrompt?.description || ''}`;

      const result = await generateImagesWithGPT(prompt, 2);
      setAiItems(result);
    } catch (error) {
      logger.error(
        'An error occurred in searching ai',
        extractErrorInfo(error),
      );
    } finally {
      setAiLoading(false);
    }
  };

  const asset = assets.find(asset => asset.key === tab);

  const removeKeywords = async (keyword: string) => {
    let updatedKeywords = keywords?.filter(el => el != keyword);
    if (setKeywords && updatedKeywords) {
      setKeywords(updatedKeywords);
      setUnsplashSearch(updatedKeywords.join(' '));
      setUnsplashPage(1);
    }
  };

  const handleArtifactSearch = (text: string) => {
    setArtifactSearchTerm(text);
  };

  const handleOrganizationArtifactSearch = (text: string) => {
    setOrgArtifactSearchTerm(text);
  };

  const handleOrganizationLogoSearch = (text: string) => {
    setOrgLogoSearchTerm(text);
  };

  const handleOrganizationVideoSearch = (text: string) => {
    setOrgVideoSearchTerm(text);
  };

  const handleStoryVideoSearch = (text: string) => {
    setStoryVideoSearchTerm(text);
  };

  const renderKeyWordSearch = () => (
    <SearchWrapper keywords={keywords}>
      <SearchInput
        iconRight={true}
        placeholder="Search key words"
        handleAction={handleSearchStock}
        radius={searchBarRadius}
      />
      {hasStocks && (
        <KeywordsWrapper>
          {keywords?.map(keyword => {
            return (
              <Keyword>
                <span className="text"> {keyword?.trim()}</span>
                <div className="close" onClick={() => removeKeywords(keyword)}>
                  <CloseIcon
                    width="7"
                    height="8"
                    strokeColor="var(--text-secondary)"
                  />
                </div>
              </Keyword>
            );
          })}
        </KeywordsWrapper>
      )}
    </SearchWrapper>
  );

  const renderAiSearch = () => (
    <SearchWrapper>
      <SearchInput
        iconRight={true}
        hideClearAll
        placeholder="Search key words"
        handleAction={handleAiSearch}
        initialValue={props.dallePrompt}
        radius={searchBarRadius}
      />
    </SearchWrapper>
  );

  const renderArtifactSearch = () => (
    <SearchWrapper>
      <SearchInput
        iconRight={true}
        hideClearAll
        placeholder="Search a photo's description"
        handleAction={handleArtifactSearch}
        autoSubmitDelay={250}
        radius={searchBarRadius}
      />
    </SearchWrapper>
  );

  const renderOrganizationArtifactSearch = () => (
    <SearchWrapper>
      <SearchInput
        iconRight={true}
        hideClearAll
        placeholder="Search a photo's description"
        handleAction={handleOrganizationArtifactSearch}
        autoSubmitDelay={250}
        radius={searchBarRadius}
      />
    </SearchWrapper>
  );

  const renderOrganizationLogoSearch = () => (
    <SearchWrapper>
      <SearchInput
        iconRight={true}
        hideClearAll
        placeholder="Search a logo's description"
        handleAction={handleOrganizationLogoSearch}
        autoSubmitDelay={250}
        radius={searchBarRadius}
      />
    </SearchWrapper>
  );

  const renderOrganizationVideoSearch = () => (
    <SearchWrapper>
      <SearchInput
        iconRight={true}
        hideClearAll
        placeholder="Search a video's description"
        handleAction={handleOrganizationVideoSearch}
        autoSubmitDelay={250}
        radius={searchBarRadius}
      />
    </SearchWrapper>
  );

  const renderStoryVideoSearch = () => (
    <SearchWrapper>
      <SearchInput
        iconRight={true}
        hideClearAll
        placeholder="Search a video's description"
        handleAction={handleStoryVideoSearch}
        autoSubmitDelay={250}
        radius={searchBarRadius}
      />
    </SearchWrapper>
  );

  const renderItemList = (asset: Asset<PhotoTab>) => (
    <Category origin={props.origin}>
      <ItemList>
        {asset.value.map((s, i) => (
          <div key={s.url}>
            <EnlargedImageRenderProp
              src={s.url}
              direction={i % 3 === 0 ? 'right' : 'left'}
              isLogo={asset.key === PhotoTab.logo}
              {...(asset.key === PhotoTab.logo && { enlargedPhotoHeight: 150 })}
            >
              {({ enlargedState, setEnlargedState, renderEnlargedImage }) => (
                <>
                  <ImageWrapper
                    isLogo={asset.key === PhotoTab.logo}
                    selected={props.selectedImageUrl === s.url}
                    onClick={() => {
                      if (props.selectedImageUrl === s.url) return null;
                      props.setSelectedImage({
                        ...s,
                        type: asset.key.toLowerCase(),
                      } as ImageWithType[ImageKey]);
                    }}
                    onMouseEnter={e => {
                      setEnlargedState(e.currentTarget.getBoundingClientRect());
                    }}
                    onMouseMove={e => {
                      if (!enlargedState) {
                        setEnlargedState(
                          e.currentTarget.getBoundingClientRect(),
                        );
                      }
                    }}
                    onMouseLeave={() => {
                      setEnlargedState(null);
                    }}
                  >
                    {props.selectedImageUrl === s.url && <SelectedCheckBadge />}
                    {asset.key === PhotoTab.quote ? (
                      <ShareableImageView
                        background={s.url}
                        width="100%"
                        height="100%"
                        imageRef={null}
                        quote={(s as QuoteImg).quote}
                        fontSize="10px"
                        lineHeight="12px"
                        quoteSize={{ width: '14px', height: '12px' }}
                        storyTellerDim={0.5}
                      />
                    ) : asset.key === PhotoTab.storyVideos ||
                      asset.key === PhotoTab.orgVideos ? (
                      <Video src={s.url} title={s.description} />
                    ) : (
                      <Image
                        loading="lazy"
                        src={applyImageOptimizations(s.url, { width: 140 })}
                        alt={s.alt}
                        type={asset.key}
                      />
                    )}
                  </ImageWrapper>
                  {enlargedState && renderEnlargedImage()}
                </>
              )}
            </EnlargedImageRenderProp>
            {props.showDescription && (
              <Description>{s.description}</Description>
            )}
            {renderImgLevelAttribution(s, asset.key)}
          </div>
        ))}
      </ItemList>
      {renderTabLevelAttribution(asset)}
      {asset.key === PhotoTab.stock && unsplashSearch && (
        <LoadMoreButton
          disabled={stockLoading}
          onClick={() => setUnsplashPage(cur => cur + 1)}
        >
          Load More Photos
        </LoadMoreButton>
      )}
    </Category>
  );

  const renderSpinner = (text: string) => (
    <SpinningLoading
      customStyle={{ top: 0 }}
      Ico={<PhotoIcon strokeColor="var(--primary)" strokeWidth={2} />}
      text={text}
    />
  );

  const renderTabLevelAttribution = (
    asset: Asset<PhotoTab>,
  ): React.ReactNode => {
    if (!asset?.value?.some(v => v.attribution)) return null;

    switch (asset.key) {
      case PhotoTab.stock:
        return (
          <AttributionRow>
            <UnsplashAttribution />
          </AttributionRow>
        );
      default:
        return null;
    }
  };

  const renderImgLevelAttribution = (
    img: Img,
    assetKey: Asset<PhotoTab>['key'],
  ): React.ReactNode => {
    if (!img.attribution) return null;

    switch (assetKey) {
      case PhotoTab.stock:
        return (
          <AuthorAttribution
            author={img.attribution.author}
            link={img.attribution.link}
          />
        );
      default:
        return null;
    }
  };

  /**
   * The search term filtering the current tab, if any. An empty result means
   * two different things — this library is empty, or this search matched
   * nothing — and the message has to say which.
   */
  const activeSearchTerm = (): string => {
    switch (tab) {
      case PhotoTab.artifact:
        return artifactSearchTerm;
      case PhotoTab.orgArtifact:
        return orgArtifactSearchTerm;
      case PhotoTab.logo:
        return orgLogoSearchTerm;
      case PhotoTab.orgVideos:
        return orgVideoSearchTerm;
      case PhotoTab.storyVideos:
        return storyVideoSearchTerm;
      case PhotoTab.stock:
        return unsplashSearch;
      default:
        return '';
    }
  };

  const emptyStateMessage = (): string => {
    // A search is running and matched nothing — say so, rather than claiming
    // the library is empty when the user has simply filtered it down to zero.
    if (activeSearchTerm()) return 'No results match your search.';

    switch (tab) {
      case PhotoTab.quote:
        return 'No shareable images are available yet.';
      case PhotoTab.logo:
        return 'No logos are available yet.';
      case PhotoTab.storyVideos:
      case PhotoTab.orgVideos:
        return 'No videos are available yet.';
      case PhotoTab.artifact:
      case PhotoTab.orgArtifact:
      case PhotoTab.headshot:
        return 'No images are available yet — upload one to get started.';
      case PhotoTab.stock:
        return 'Search the stock library to see photos.';
      default:
        return 'Describe the photo you want to see and press Enter.';
    }
  };

  const renderContent = () => {
    if (unsplashPage === 1 && stockLoading && tab === PhotoTab.stock) {
      return renderSpinner('Loading stock images...');
    } else if ((!hasAis || aiLoading) && tab === PhotoTab.ai) {
      return renderSpinner('Loading Ai images...');
    } else if (asset?.value?.length) {
      return renderItemList(asset);
    } else {
      return <EmptyState>{emptyStateMessage()}</EmptyState>;
    }
  };

  return (
    <Main onKeyDown={e => e.stopPropagation()}>
      {props.openPrevModal && (
        <Back type="button" onClick={handleGoBack} aria-label="Back">
          <BackIcon strokeColor="currentColor" />
        </Back>
      )}

      {TopComponent && (
        <TopComponent
          tab={tab}
          onHeadshotUpload={async (file: File) => {
            const speakerId = uploadSpeakerId || headshotSpeakers[0]?.id;
            if (!speakerId) return;
            if (!uploadSpeakerId) setUploadSpeakerId(speakerId);
            await handleHeadshotUpload(file, speakerId);
          }}
          isUploadingHeadshot={isUploadingHeadshot}
        />
      )}

      <TabsInset>
        <Tabs<PhotoTab>
          tabs={tabHeader.map(t => ({ id: t, label: TAB_LABELS[t] ?? t }))}
          activeTab={tab}
          onChange={t => {
            setTab(t);
            props.setSelectedImage(null);
          }}
        />
      </TabsInset>

      {tab === PhotoTab.artifact && renderArtifactSearch()}
      {tab === PhotoTab.stock && renderKeyWordSearch()}
      {tab === PhotoTab.ai && renderAiSearch()}
      {tab === PhotoTab.orgArtifact && renderOrganizationArtifactSearch()}
      {tab === PhotoTab.logo && renderOrganizationLogoSearch()}
      {tab === PhotoTab.orgVideos && renderOrganizationVideoSearch()}
      {tab === PhotoTab.storyVideos && renderStoryVideoSearch()}

      {tab === PhotoTab.headshot && headshotSpeakers.length > 0 && (
        <HeadshotUploadRow>
          <SpeakerSelect
            value={uploadSpeakerId}
            onChange={e => setUploadSpeakerId(e.target.value)}
          >
            <option value="">Upload to speaker...</option>
            {headshotSpeakers.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </SpeakerSelect>
        </HeadshotUploadRow>
      )}

      {props.isLoading && renderSpinner('Saving Ai photo. Please wait...')}

      <Content origin={props.origin}>
        {renderContent()}
        <ContentBottom>
          {BottomComponent && <BottomComponent tab={tab} />}
        </ContentBottom>
      </Content>
    </Main>
  );
});

export default PhotoModal;

/* Flex column so PhotoModalTop's Replace footer (`order: 1`) sorts to the
   bottom of the modal body — see FooterBar in PhotoModalTop.tsx. */
const Main = styled.div`
  display: flex;
  flex-direction: column;
`;

/* Mirrors the shared Modal's close button (see docs/design-system.md →
   "Close Buttons") in the opposite corner. */
const Back = styled.button`
  position: absolute;
  top: var(--spacing-sm);
  left: var(--spacing-sm);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-sm);
  color: var(--text-muted);
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;

const Content = styled.div<{ origin?: string }>`
  position: relative;
  height: 300px;
  overflow: auto;
  display: flex;
  flex-direction: column;

  ${props =>
    props.origin === 'quotecard' &&
    css`
      padding: 0 ${PICKER_MODAL_INSET};
      max-height: 400px;
      min-height: 340px;
    `}
`;

const ContentBottom = styled.div`
  margin-top: auto;
  padding: 0 ${PICKER_MODAL_INSET};
`;

const SearchWrapper = styled.div<{ keywords?: string[] }>`
  padding: 0 ${PICKER_MODAL_INSET};
  margin-bottom: var(--spacing-sm);
  display: flex;
  flex-direction: column;
  gap: ${props => props.keywords?.length && 'var(--spacing-md)'};
`;

const Category = styled.div<{ origin?: string }>`
  text-align: left;

  ${props =>
    props.origin !== 'quotecard' &&
    css`
      margin: 5px 0;
      overflow: auto;
      padding: 0 ${PICKER_MODAL_INSET};
      flex: 1;
    `}
`;

const ItemList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 12px;

  & > div {
    min-width: 0;
  }
`;
/* 16:9 like every other card in the picker family — the frame these assets end
   up inside on the canvas. */
const ImageWrapper = styled.div<{ isLogo?: boolean; selected?: boolean }>`
  aspect-ratio: 16 / 9;
  box-sizing: border-box;
  border-radius: var(--radius-input);
  position: relative;
  cursor: pointer;
  overflow: hidden;
  /* Green border on the pick, matching the template cards in the Replace
     Captions / Intro Card modals; the gold check badge carries the selection
     itself (see SelectedCheckBadge). */
  border: 1px solid
    ${props => (props.selected ? 'var(--primary)' : 'var(--border)')};
  background-color: ${props =>
    props.isLogo ? 'var(--gray-400)' : 'var(--surface)'};
  transition: border-color var(--transition-normal);

  &:hover {
    border-color: var(--primary);
  }
`;

const Image = styled.img<{ type: PhotoTab }>`
  width: 100%;
  height: 100%;
  object-fit: ${props => (props.type === PhotoTab.logo ? 'contain' : 'cover')};
  object-position: ${props =>
    props.type === PhotoTab.logo
      ? 'center'
      : props.type !== PhotoTab.quote && 'top'};
  ${props => props.type === PhotoTab.logo && 'padding: 4px;'}
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
`;

/* Secondary-tier button (see docs/design-system.md → "Button Hierarchy"),
   at md height since it sits inside the content area, not a footer. */
const LoadMoreButton = styled.button`
  margin: var(--spacing-lg) auto var(--spacing-xs);
  display: inline-flex;
  height: var(--btn-h-md);
  padding: 0 var(--btn-px-md);
  justify-content: center;
  align-items: center;
  gap: var(--spacing-sm);
  border-radius: var(--radius-button);
  background: transparent;
  color: var(--foreground);
  border: 1px solid var(--border);
  cursor: pointer;
  font-weight: var(--font-weight-medium);
  font-size: var(--text-sm);
  outline: 0;
  transition: var(--transition-normal);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Description = styled.div`
  margin-top: var(--spacing-xs);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`;

/* The shared Tabs' list carries --spacing-sm side padding of its own, so this
   subtracts it to land the labels on the same inset as every other row. */
const TabsInset = styled.div`
  padding: 0 calc(${PICKER_MODAL_INSET} - var(--spacing-sm));
`;

/* Empty state per docs/design-system.md → "Empty States": muted --text-sm
   prose, centered in the content area so the emptiness reads as intentional. */
const EmptyState = styled.div`
  flex: 1;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 ${PICKER_MODAL_INSET};
  color: var(--text-muted);
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);
`;

const Keyword = styled.span`
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  border: 1px solid var(--border);
  border-radius: 40px;
  padding: 8px 16px;
  align-items: center;
  white-space: nowrap;
  display: flex;
  gap: 5px;
  .close {
    cursor: pointer;
  }
`;

const KeywordsWrapper = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-start;
  width: 100%;
  padding-left: 5px;
  overflow: auto;
`;

const AttributionRow = styled.div`
  margin: 24px 0 8px 0;
  display: flex;
  justify-content: center;
`;

const HeadshotUploadRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 ${PICKER_MODAL_INSET};
  margin-bottom: 10px;
  align-items: center;
`;

const SpeakerSelect = styled.select`
  flex: 1;
  height: 32px;
  padding: 4px 8px;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  color: var(--foreground);
  font-size: var(--text-xs);

  option {
    background: var(--background);
    color: var(--foreground);
  }
`;
